import { Box, Stack, Typography, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CardContainerRetro } from "../Container/CardContainerRetro";
import { NavLink } from "react-router-dom";
import { WriterInfo } from "../WriterInfo/WriterInfo";
import LockIcon from "@mui/icons-material/Lock";
import { getFirstSentence, getPreviewTextFromThread } from "../../utils/textUtils";
import { ThreadsTitle } from "../customTypo/Display";

export const CardCollectionBook = ({
	collection,
	date,
	rd = 8,
	borderType = "full",
	isRemarkable = false,
	isRanked = false,
	rank = 0,
	isMobile,
	isMe = false,
	size = "default", // "default" | "large" | "small"
	categoryLabels = [],
}) => {
	const theme = useTheme();
	const displayHeadings = theme.other?.displayHeadings || {};

	// 사이즈별 설정 (모바일에서는 size 무시)
	const isLarge = !isMobile && size === "large";
	const isSmall = size === "small";
	const cardHeight = isSmall ? "160px" : isMobile ? "240px" : 440; // small: fixed 160px
	const titleVariant = isMobile
		? "body2"
		: isLarge
		? "h4"
		: isSmall
		? "h6"
		: "h5";
	const descriptionVariant = isMobile
		? "body2"
		: isLarge
		? "body1"
		: isSmall
		? "body2"
		: "body1";
	const writerInfoSize = isMobile
		? "small"
		: isLarge
		? "xl"
		: isSmall
		? "small"
		: "large";
	const titleHeight = isSmall ? "auto" : isMobile ? 52 : isLarge ? 72 : 72;
	const threadsVariant = isMobile ? "h6" : isLarge ? "h3" : isSmall ? "h6" : "h4";
	const descriptionHeight = isSmall ? "0px" : isMobile ? "40px" : "58px";
	const writerInfoOffset = isSmall ? theme.spacing(2) : isMobile ? theme.spacing(4.5) : theme.spacing(6);
	const displayPrimary = isMobile
		? displayHeadings.d5 || {}
		: isLarge
		? displayHeadings.d3 || {}
		: isSmall
		? displayHeadings.d5 || {}
		: displayHeadings.d4 || {};

	return (
		<NavLink
			to={
				isMe
					? `/my-collection/${collection.id}`
					: `/collection/${collection.id}`
			}
			style={{
				width: "100%",
				maxWidth: "100%",
				display: "block",
				boxSizing: "border-box",
			}}
		>
			<CardContainerRetro
				w={"100%"}
				rd={rd}
				bg={theme.palette.background.paper}
				borderType={borderType}
			>
				{/* 비공개 컬렉션 자물쇠 아이콘 */}
				{collection.isPublic === false && (
					<Box
						sx={{
							position: "absolute",
							top: 16,
							right: 16,
							zIndex: 10,
						}}
					>
						<LockIcon
							sx={{
								fontSize: 20,
								color: "black",
							}}
						/>
					</Box>
				)}
				<Stack
					spacING={isMobile ? 4 : 6}
					sx={{
						padding: isMobile ? theme.spacing(2) : theme.spacing(3),
						width: "100%",
						height: cardHeight,
						minHeight: isSmall ? "160px" : isMobile ? "auto" : cardHeight,
						borderRadius: theme.spacing(0.5),
						position: "relative",
						backgroundColor: theme.palette.background.paper,
						transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
						cursor: 'pointer',
						'.contentBlock': { opacity: 1, transition: 'opacity 150ms ease-in-out' },
						'&:hover': {
							backgroundColor: theme.palette.primary.main,
							transform: 'none',
							'& *': { color: theme.palette.background.paper + ' !important' },
							'& svg': { color: theme.palette.background.paper + ' !important' },
							'& .tocOverlay': { opacity: 1 },
							'& .contentBlock': { opacity: 0 },
						},
						pb: isSmall ? theme.spacing(2) : isMobile ? theme.spacing(4.5) : theme.spacing(6),
						minWidth: 0,
					}}
				>
						<Stack spacing={0.5} className="contentBlock">
						<ThreadsTitle
							variant={threadsVariant}
							fw={displayPrimary.fontWeight || 900}
								sx={{
									width: "100%",
									color: theme.palette.text.primary,
									overflow: "hidden",
									wordBreak: "keep-all",
									overflowWrap: "anywhere",
									textOverflow: "ellipsis",
									minWidth: 0,
								}}
							>
								{collection.title}
							</ThreadsTitle>
						{!isMobile && !isSmall && (
							<Typography
								variant={descriptionVariant}
								sx={{
									height: descriptionHeight,
									// color: theme.customTokens.colors.custom.ui.dark.bold,
									overflow: "hidden",
									wordBreak: "break-word",
									overflowWrap: "anywhere",
									display: "-webkit-box",
									WebkitLineClamp: 2,
									WebkitBoxOrient: "vertical",
									minWidth: 0,
								}}
							>
								{collection.description}
							</Typography>
						)}
						{!isMobile &&
						!isSmall &&
						Array.isArray(categoryLabels) &&
						categoryLabels.length > 0 ? (
							<Box
								sx={{
									display: "flex",
									flexWrap: "wrap",
									gap: 0.5,
									mt: "24px !important",
									ml: "-4px !important",
								}}
							>
								{categoryLabels.map((label) => (
									<Chip
										key={label}
										label={label}
										size={isMobile ? "small" : "medium"}
										variant="outlined"
									/>
								))}
							</Box>
						) : null}
					</Stack>
					{isSmall ? (
						<Box sx={{ mt: isMobile ? 1 : 1.5 }}>
							<WriterInfo
								size={writerInfoSize}
								profileImage={collection.profileImage}
								userID={collection.userID}
								username={collection.username}
								name={collection.name}
								showUserPageLink={!isMe}
							/>
						</Box>
					) : (
						<Box
							sx={{
								position: "absolute",
								left: isMobile ? 8 : 16,
								right: isMobile ? 8 : 16,
								bottom: isMobile ? 8 : 12,
								display: "flex",
								boxSizing: "border-box",
								mb: isMobile ? 1 : 2,
							}}
						>
							<WriterInfo
								size={writerInfoSize}
								profileImage={collection.profileImage}
								userID={collection.userID}
								username={collection.username}
								name={collection.name}
								showUserPageLink={!isMe}
							/>
						</Box>
					)}

					{/* toc overlay area */}
					{Array.isArray(collection?.threadList) && collection.threadList.length > 0 ? (
						<Box
							className="tocOverlay"
							sx={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: writerInfoOffset,
								opacity: 0,
								transition: "opacity 150ms ease-in-out",
								pointerEvents: "none",
								display: "flex",
								flexDirection: "column",
								justifyContent: "flex-start",
								p: isMobile ? 2 : 3,
								overflow: 'hidden',
							}}
						>
							<Stack spacing={0.75} sx={{ overflow: "hidden" }}>
                                {collection.threadList.slice(0, isMobile ? 4 : 6).map((th, i) => (
									<Typography
										key={th.id || i}
										variant={isMobile ? "caption" : "body2"}
										sx={{
											fontWeight: 700,
											lineHeight: 1.4,
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
										}}
									>
                                        {i + 1}. {getPreviewTextFromThread(th, isMobile ? 60 : 80) || (th?.id ? `Thread ${th.id}` : "")}
									</Typography>
								))}
							</Stack>
						</Box>
					) : null}
				</Stack>
			</CardContainerRetro>
		</NavLink>
	);
};
